import { tenantSelector } from '@/features/configuration/configurationSelector';
import {
  type I18nLanguage,
  localeConfigurations,
  useTranslation,
} from '@/i18n';
import { useSelector } from '@/store/utils';
import { cn } from '@/utils/cn';
import { typedEntries } from '@/utils/object';
import { getTenantConfiguration } from '@/utils/tenant';
import { Button, Icons, Popover, Typography } from '@fleet/webshop-ui';
import { useTranslation as useWidgetTranslation } from '@turnit-ride-ui/webshop-search-widget';
import type { FC } from 'react';

interface RegularLanguageSelectProps {}

const RegularLanguageSelect: FC<RegularLanguageSelectProps> = () => {
  const {
    i18n: { changeLanguage, language: currentLanguage },
  } = useTranslation();
  const {
    i18n: { changeLanguage: changeWidgetLanguage },
  } = useWidgetTranslation();
  const currentTenant = useSelector(tenantSelector);
  const tenantConfiguration = getTenantConfiguration(currentTenant.value);

  return (
    <Popover
      trigger={
        <Button
          data-testid="language-select-trigger"
          variant="ghostLink"
          className="group flex gap-2"
        >
          <Icons.globe height={24} width={24} />
          {localeConfigurations[currentLanguage as I18nLanguage].label}
          <Icons.chevronDown
            height={16}
            width={16}
            className="transition-transform duration-200 group-aria-expanded:rotate-180"
          />
        </Button>
      }
      content={
        <>
          {typedEntries(localeConfigurations)
            .filter(([code]) =>
              tenantConfiguration?.languageOptions.includes(code)
            )
            .map(([code, config], key) => (
              <div
                key={key}
                className={cn(
                  'flex h-10 w-full items-center justify-between px-3 py-2 text-dark hover:cursor-pointer hover:bg-primary-pale',
                  code === currentLanguage && 'bg-primary-light'
                )}
                onClick={() => {
                  changeLanguage(code);
                  changeWidgetLanguage(code);
                }}
                data-testid={`language-select-choose-${code}`}
              >
                <Typography variant="body1">{config.label}</Typography>
                {code === currentLanguage && (
                  <Icons.checkCircle
                    height={16}
                    width={16}
                    className="text-success-text"
                  />
                )}
              </div>
            ))}
        </>
      }
      placement="bottom end"
      data-testid="language-select-popover"
      className={{ root: 'w-40', content: 'p-0' }}
    />
  );
};

export default RegularLanguageSelect;
